import styles from "../../../Dashboard.module.scss";
import CharacterInput from "../../../../../components/Input/CharacterInput";
import searchIcon from "../../../../../resources/images/search.svg";
import keyboard_arrow_right from "../../../../../resources/images/keyboard_arrow_right.svg";
import { INPUT_TYPES } from "../../../../../utils/web-only-constants";
import ToggleSelect from "../../../../../shared/shared-with-mobile/components/ToggleSelect/ToggleSelect";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { ReadinessScoresContext } from "../../../../../shared/shared-with-mobile/providers/readinessScores.provider";
import { PlayersContext } from "../../../../../shared/shared-with-mobile/providers/players.provider";
import { DashboardStatsGrid } from "../../../../../components/DashboardStatsGrid/DashboardStatsGrid";
import dashboardStatsStyles from "../../../../../components/DashboardStatsGrid/DashboardStatsGrid.module.scss";
import Avatar from "../../../../../shared/shared-with-mobile/components/PlayerAvatar/Avatar";
import { isEmpty } from "lodash";
import ReadinessGraph from "../../../../../shared/shared-with-mobile/dashboard-widgets/TeamReadiness/ReadinessGraph";
import { InstallsContext } from "../../../../../shared/shared-with-mobile/providers/installs.provider";
import { InstallModel } from "../../../../../generated/from-api/models/install.model";
import InstallPreview from "../../../../Installs/install-components/InstallPreview/InstallPreview";
import {
  doPositionsOverlap,
  formatReadinessByUnitChartData,
  hasPositionType,
  PlayerRow,
  PositionFiltersMap,
} from "../../../../../shared/shared-with-mobile/functions";
import { getAbbreviatedDisplayName } from "../../../../../shared/shared-with-mobile/utilities/getAbbreviatedName";
import PlayerModal from "../../../Components/PlayerModal/PlayerModal";
import { BarDatum } from "@nivo/bar";
import PhonePreview from "../../../../../components/PhonePreview/PhonePreview";
import { PlayerModel } from "../../../../../generated/from-api/models/relationships/player.model";

interface Props {
  lessonId: string;
  close: () => void;
}

const LessonLessons: React.FC<Props> = ({ lessonId, close }) => {
  const chartDefaultDataForUnit: BarDatum[] = [
    { category: "Offense", averageScore: 0 },
    { category: "Defense", averageScore: 0 },
    { category: "S.T.", averageScore: 0 },
  ];
  const [chartDataForUnit, setChartDataForUnit] = useState<BarDatum[]>(
    chartDefaultDataForUnit
  );

  const { playerInstallMetrics } = useContext(ReadinessScoresContext);
  const { currentPlayersAsArray } = useContext(PlayersContext);
  const { installs, globalInstalls } = useContext(InstallsContext);
  const [selectedFilter, setSelectedFilter] = useState(PositionFiltersMap[0]);
  const [showPlayerModal, setShowPlayerModal] = useState<boolean>(false);
  const [player, setPlayer] = useState<PlayerModel>();

  const [searchText, setSearchText] = useState("");
  const [install, setInstall] = useState<InstallModel | null>(null);

  const [dataForPlayerStatsGrid, setDataForPlayerStatsGrid] = useState<
    PlayerRow[]
  >();

  useEffect(() => {
    searchGridResult(searchText);
  }, [selectedFilter]);

  useEffect(() => {
    const matchInstall =
      installs.find((install) => install.id === lessonId) ||
      globalInstalls.find((install) => install.id === lessonId);

    if (matchInstall) {
      setInstall(matchInstall);
    } else {
      setInstall(null);
    }
  }, [installs, globalInstalls, lessonId]);

  useEffect(() => {
    if (
      currentPlayersAsArray &&
      !isEmpty(currentPlayersAsArray) &&
      playerInstallMetrics &&
      !isEmpty(playerInstallMetrics)
    ) {
      const playerRows = playerInstallMetrics
        .filter(
          (p) =>
            p.installId === lessonId &&
            doPositionsOverlap(p.installPositions, p.playerPositions)
        )
        .map((p) => {
          return {
            playerFirstName: p.playerFirstName,
            playerLastName: p.playerLastName,
            playerId: p.playerId ? p.playerId : "",
            playerUserId: p.playerUserId,
            positions: p.playerPositions ? p.playerPositions : "",
            readiness: Number(p.readinessScore),
          };
        });

      setChartDataForUnit(formatReadinessByUnitChartData(playerRows));

      setDataForPlayerStatsGrid(playerRows);
    }
  }, [currentPlayersAsArray, playerInstallMetrics]);

  const searchGridResult = (event: any) => {
    setSearchText(event);
  };

  const onRowClick = (id: string) => {
    setPlayer(
      currentPlayersAsArray.find((player: PlayerModel) => player.id === id)
    );
    setShowPlayerModal(true);
  };

  const columnsForPlayers = useMemo(
    () => [
      {
        name: "PLAYER",
        selector: (row: PlayerRow) => row.playerId,
        cell: (row: PlayerRow) => {
          return (
            <div
              className={`${dashboardStatsStyles.clickableCell} ${dashboardStatsStyles.flexRow}`}
              onClick={() => onRowClick(row.playerId)}
            >
              <div className={dashboardStatsStyles.usreListImg}>
                <Avatar userId={row.playerId} small type="player" />
              </div>

              <span className={dashboardStatsStyles.playerName}>
                {getAbbreviatedDisplayName(
                  row.playerFirstName,
                  row.playerLastName
                )}
              </span>
              {row.positions && (
                <div className={dashboardStatsStyles.playerPosition}>
                  {row.positions}
                </div>
              )}
            </div>
          );
        },
        sortable: true,
        grow: 2,
      },
      {
        name: "LESSON READINESS",
        selector: (row: PlayerRow) => +row.readiness,
        format: (row: PlayerRow) => `${row.readiness.toFixed(2)}%`,
        sortable: true,
        right: true,
      },
    ],
    [currentPlayersAsArray]
  );

  return (
    <div>
      <p className={styles.breadcrumb}>
        <span className={styles.drillInTitle} onClick={() => close()}>
          Lesson Readiness
        </span>
        <img
          src={keyboard_arrow_right}
          alt="keyboard_arrow_right"
          className={styles.arrowRight}
        />
        {install?.name}
      </p>

      <div className={styles.chartCardRow}>
        <div className={styles.media}>
          <PhonePreview containerClass={styles.media} heightRatio={0.85}>
            <InstallPreview slides={install ? install.slides : []} />
          </PhonePreview>
        </div>
        <div className={styles.graphs}>
          <ReadinessGraph
            title="Average Lesson Readiness"
            type="Line"
            xLabel="Date"
            yLabel="Readiness"
            ySymbol="%"
            data={chartDataForUnit}
            indexBy="category"
            valueKey="averageScore"
          />

          <ReadinessGraph
            title="Lesson Readiness by Unit"
            type="Bar"
            xLabel="Unit"
            yLabel="Readiness"
            ySymbol="%"
            data={chartDataForUnit}
            indexBy="category"
            valueKey="averageScore"
          />
        </div>
      </div>

      <div className={styles.searchAndFilterBtnForDrillIn}>
        <div>
          <CharacterInput
            type={INPUT_TYPES.TEXT}
            placeholder="Search"
            id="search"
            size="x-small"
            icon={searchIcon}
            clearButton
            value={searchText}
            onChange={(e) => searchGridResult(e.currentTarget.value)}
          />
        </div>

        <div className={styles.filterBtns}>
          <ToggleSelect
            options={PositionFiltersMap.map((f) => f.label)}
            selectedOption={selectedFilter.label}
            background="#151724"
            borderColor="#151724"
            onChange={(label: string) => {
              const newFilter = PositionFiltersMap.find(
                (f) => f.label == label
              );
              if (newFilter) {
                setSelectedFilter(newFilter);
              }
            }}
          />
        </div>
      </div>
      <br />

      <div>
        <>
          <DashboardStatsGrid
            data={dataForPlayerStatsGrid?.filter((m) => {
              return (
                hasPositionType(
                  m.positions ? m.positions.split("|") : [],
                  selectedFilter.value
                ) &&
                (
                  "" +
                  m.playerFirstName +
                  m.playerLastName +
                  m.readiness.toFixed(2) +
                  getAbbreviatedDisplayName(m.playerFirstName, m.playerLastName)
                )
                  .toLowerCase()
                  .indexOf(searchText ? searchText.toLowerCase() : "", 0) !== -1
              );
            })}
            columns={columnsForPlayers}
          />
          {player && (
            <PlayerModal
              player={player}
              visible={showPlayerModal}
              defaultTab={"lessons"}
              close={() => setShowPlayerModal(false)}
            />
          )}
        </>
      </div>
    </div>
  );
};

export default LessonLessons;
